// AGENT-MENTION (06 §Agents in chat; 02 `wakeup_request`; 05 §Delegation): mentioning an agent
// or handing it a task never silently does nothing. Under a message (and in a task's drawer)
// each agent it asked gets one line: its name, what became of the ask (Queued, Running, Waiting,
// Done, Failed — or why nothing started: Not live yet, Can't see this channel, Paused, Over
// budget, Channel is silent, Not started) and, when nothing started, the reason. Two actions,
// each behind the API's own checks:
//   - "Add Riley to #channel" (the readiness route's `can_add`: the channel's managers and
//     company admins, an active DE that cannot read it) adds it through `channels.addMember`,
//     then asks again for this message;
//   - "Ask again" (the author, the channel's managers, admins) re-runs the ask once its cause is
//     fixed (resumed, live, the rule changed).
// The line updates live: a wake-up or run change arrives as the message's own payload ping.
//
// CHAT-DE (Slack's "isn't in this channel" prompt): right after its author sent it from this
// tab, a message that asked a DE who is not a member of the channel shows, under the wake line
// and to the author only, "Riley isn't in #brief. Add them? [Add] [Just this once] [No]".
// Add makes it a member (`channels.addMember`; any poster on a public channel, any member on a
// private one: the row's `can_invite`) and, when the DE could not read the channel, asks again
// for this message; it then wakes on @mentions by the channel's rule. "Just this once" leaves it
// out — offered only when the DE reads the channel anyway (the readiness row's `can_read`, apart
// from a silent rule or a pause); otherwise the prompt says it "can't see" the channel. "No"
// stops the prompt for that DE in that channel (per company and viewer).
//
// REACH-S1: a DE that does not take asks from the author ("Needs access") offers the author
// "Ask for access" (features/agents/ask-access.tsx); once granted, "Ask again" asks it.
import { Badge, Button, cn, InfoTip } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BotIcon, LoaderCircleIcon, RotateCwIcon, UserPlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { updateMessage } from "../../live/cache-patch";
import { AskAccessButton } from "../agents/ask-access";
import { isManager } from "../company";
import {
  answerPrompt,
  declineInChannel,
  promptOpen,
  sentFromHere,
  useAskedHere,
  viewerScope,
} from "./asked-here";
import { useCanInvite, useMemberRefresh } from "./channel-agents";
import { workingAgents } from "./chat-model";
import type { AgentWake, MessageView } from "./chat-queries";
import { chatKeys, membersQuery, threadAgentsQuery } from "./chat-queries";
import type { ChatContext } from "./use-chat";

type State = AgentWake["state"];
type Variant = "info" | "success" | "warning" | "danger" | "neutral";

/** The label and tone of each state (shared with the task drawer and the @ menu). */
export const WAKE_STATES: Record<State, { label: string; variant: Variant }> = {
  queued: { label: "Queued", variant: "info" },
  running: { label: "Running", variant: "info" },
  waiting: { label: "Waiting", variant: "warning" },
  done: { label: "Done", variant: "success" },
  failed: { label: "Failed", variant: "danger" },
  not_live: { label: "Not live yet", variant: "warning" },
  no_access: { label: "Can't see this channel", variant: "warning" },
  paused: { label: "Paused", variant: "warning" },
  over_budget: { label: "Over budget", variant: "danger" },
  silent: { label: "Channel is silent", variant: "neutral" },
  blocked: { label: "Not started", variant: "warning" },
  // REACH-S1: outside the DE's Hears from; "Ask for access" sends the request.
  not_in_reach: { label: "Needs access", variant: "warning" },
};

/**
 * MID-RUN-STEER: where a message went while its agent was busy or about to start — folded into
 * the turn it was working on, or waiting (batched) for its next one.
 */
export const DELIVERY: Record<NonNullable<AgentWake["delivery"]>, string> = {
  running_turn: "sent to running turn",
  next_turn: "queued for next turn",
};

/** States in which nothing started: their reason shows, and the ask may be made again. */
export const NOT_STARTED: ReadonlySet<State> = new Set<State>([
  "not_live",
  "no_access",
  "paused",
  "over_budget",
  "silent",
  "blocked",
  "not_in_reach",
]);

/** One agent's line: name, state badge, the reason when nothing started, and actions. */
export function AgentWakeLine({
  wake,
  actions,
  className,
}: {
  wake: Pick<AgentWake, "name" | "state" | "reason"> & Partial<Pick<AgentWake, "delivery">>;
  actions?: React.ReactNode;
  className?: string;
}) {
  const s = WAKE_STATES[wake.state];
  const quiet = !NOT_STARTED.has(wake.state) && wake.state !== "failed";
  const delivery = wake.delivery ? DELIVERY[wake.delivery] : null;
  return (
    <div
      data-slot="agent-wake"
      data-state={wake.state}
      className={cn("flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs", className)}
    >
      <BotIcon aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="font-medium text-foreground">{wake.name}</span>
      <Badge variant={s.variant} dot title={wake.reason}>
        {s.label}
      </Badge>
      {delivery ? (
        <span data-slot="wake-delivery" className="text-muted-foreground">
          {delivery}
        </span>
      ) : null}
      {quiet ? (
        <span className="sr-only">{wake.reason}</span>
      ) : (
        <span className="min-w-0 text-muted-foreground">{wake.reason}</span>
      )}
      {actions}
    </div>
  );
}

/** The wake results under a message, with "Add to #channel" and "Ask again". */
export function MessageAgentWakes({ chat, message }: { chat: ChatContext; message: MessageView }) {
  const wakes = message.agent_wakes ?? [];
  useAskedHere();
  // CHAT-DE: the prompt's DEs may be asked about only on a message this tab just sent.
  const sentHere =
    sentFromHere(message.client_id) && wakes.some((w) => w.agent_kind === "digital_employee");
  const needsReadiness = wakes.some((w) => w.state === "no_access") || sentHere;
  const agents = useQuery({
    ...threadAgentsQuery(chat.api, chat.co, message.thread_id),
    enabled: needsReadiness && chat.meId !== null,
  });
  const queryClient = useQueryClient();
  const replace = (next: AgentWake) =>
    updateMessage(queryClient, chat.co, message.thread_id, message.id, (m) => ({
      ...m,
      agent_wakes: (m.agent_wakes ?? []).map((w) =>
        w.membership_id === next.membership_id ? next : w,
      ),
    }));
  const wakeAgain = (membershipId: string) =>
    chat.api.client.messages.wake({
      params: { co: chat.co, id: message.thread_id, mid: message.id },
      body: { membership_id: membershipId },
    });
  const ask = useMutation({
    mutationFn: wakeAgain,
    onSuccess: replace,
  });
  // The agents this view added (an add stands even when the ask after it fails: no second Add,
  // whatever a stale readiness read says; Ask again retries the ask).
  const [added, setAdded] = useState<ReadonlySet<string>>(() => new Set());
  const noteAdded = (id: string) => setAdded((prev) => new Set(prev).add(id));
  const add = useMutation({
    mutationFn: async (v: { channelId: string; membershipId: string }) => {
      await chat.api.client.channels.addMember({
        params: { co: chat.co, id: v.channelId },
        body: { membership_id: v.membershipId },
      });
      noteAdded(v.membershipId);
      return wakeAgain(v.membershipId);
    },
    onSuccess: (next, v) => {
      replace(next);
      void queryClient.invalidateQueries({ queryKey: chatKeys.agents(chat.co, message.thread_id) });
      void queryClient.invalidateQueries({ queryKey: chatKeys.members(chat.co, v.channelId) });
    },
  });
  // CHAT-DE: the prompt's reads — the channel's members and whether the author may add.
  const channelId = agents.data?.channel_id ?? null;
  const members = useQuery({
    ...membersQuery(chat.api, chat.co, channelId ?? ""),
    enabled: sentHere && channelId !== null,
    retry: false,
  });
  const canInvite = useCanInvite(chat, { id: channelId ?? "", archived_at: null });
  const refreshMembers = useMemberRefresh(chat, channelId ?? "");
  const join = useMutation({
    mutationFn: async (v: { channelId: string; wake: AgentWake }) => {
      await chat.api.client.channels.addMember({
        params: { co: chat.co, id: v.channelId },
        body: { membership_id: v.wake.membership_id },
      });
      noteAdded(v.wake.membership_id);
      // The add stands whatever follows: the prompt is answered and the members refresh now,
      // so a failed ask below never leaves an Add that would conflict (Ask again retries it).
      answerPrompt(message.client_id ?? "", v.wake.membership_id);
      void refreshMembers();
      // It could not be asked (could not read the channel): ask again, now that it can.
      return NOT_STARTED.has(v.wake.state) ? wakeAgain(v.wake.membership_id) : null;
    },
    onSuccess: (next) => {
      if (next) replace(next);
    },
  });
  if (!wakes.length || message.deleted_at) return null;
  const mine = message.author_membership_id !== null && message.author_membership_id === chat.meId;
  const mayAsk = !chat.readOnly && chat.meId !== null && (mine || isManager(chat.company));
  const channel = agents.data?.channel_id
    ? { id: agents.data.channel_id, name: agents.data.channel_name ?? "this channel" }
    : null;
  const inChannel = new Set(members.data?.data.map((m) => m.membership_id) ?? []);
  const scope = viewerScope(chat.co, chat.meId);
  // Whether a DE reads the channel now (its readiness row's `can_read`, apart from what else
  // stops a wake): "Just this once" only for one that does.
  const readiness = new Map(agents.data?.data.map((a) => [a.membership_id, a]) ?? []);
  const prompted = new Set(
    channel && members.isSuccess && canInvite && mine
      ? wakes
          .filter(
            (w) =>
              w.agent_kind === "digital_employee" &&
              readiness.has(w.membership_id) &&
              !added.has(w.membership_id) &&
              w.state !== "paused" &&
              w.state !== "not_live" &&
              !inChannel.has(w.membership_id) &&
              promptOpen(message.client_id, scope, channel.id, w.membership_id),
          )
          .map((w) => w.membership_id)
      : [],
  );
  // One action's error at a time: a new try clears the last one's.
  const clearErrors = () => {
    ask.reset();
    add.reset();
    join.reset();
  };
  const busy = ask.isPending || add.isPending || join.isPending;
  const failed = ask.error ?? add.error ?? join.error;
  return (
    <div data-slot="agent-wakes" className="flex min-w-0 flex-col gap-1 pt-0.5">
      {wakes.map((w) => {
        const canAdd =
          w.state === "no_access" &&
          channel !== null &&
          !prompted.has(w.membership_id) &&
          !added.has(w.membership_id) &&
          agents.data?.data.find((a) => a.membership_id === w.membership_id)?.can_add === true;
        const prompt =
          channel && prompted.has(w.membership_id) ? (
            <NotInChannelPrompt
              key={`${w.membership_id}-prompt`}
              wake={w}
              channelName={channel.name}
              busy={busy}
              onAdd={() => {
                clearErrors();
                join.mutate({ channelId: channel.id, wake: w });
              }}
              onOnce={() => answerPrompt(message.client_id ?? "", w.membership_id)}
              onNo={() => declineInChannel(scope, channel.id, w.membership_id)}
              blind={readiness.get(w.membership_id)?.can_read !== true}
            />
          ) : null;
        return [
          <AgentWakeLine
            key={w.membership_id}
            wake={w}
            actions={
              <>
                {canAdd && channel ? (
                  <Button
                    size="xs"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      clearErrors();
                      add.mutate({ channelId: channel.id, membershipId: w.membership_id });
                    }}
                  >
                    <UserPlusIcon data-icon="inline-start" aria-hidden="true" />
                    Add {w.name} to #{channel.name}
                  </Button>
                ) : null}
                {w.state === "not_in_reach" && mine && !chat.readOnly ? (
                  <AskAccessButton
                    api={chat.api}
                    co={chat.co}
                    agentId={w.membership_id}
                    agentName={w.name}
                  />
                ) : null}
                {mayAsk && NOT_STARTED.has(w.state) && !canAdd && !prompted.has(w.membership_id) ? (
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={busy}
                    aria-label={`Ask ${w.name} again`}
                    onClick={() => {
                      clearErrors();
                      ask.mutate(w.membership_id);
                    }}
                  >
                    <RotateCwIcon data-icon="inline-start" aria-hidden="true" />
                    Ask again
                  </Button>
                ) : null}
              </>
            }
          />,
          prompt,
        ];
      })}
      {failed ? (
        <p role="alert" className="text-xs text-danger-text">
          {failed instanceof Error ? failed.message : "That did not work."}
        </p>
      ) : null}
    </div>
  );
}

/**
 * CHAT-DE: "Social Media Director is working…" above the composer while an agent this
 * conversation asked has not answered: its wake-up is still queued or running (the message's
 * `agent_wakes`, updated live) and no message of its own has landed since (`workingAgents`).
 * Agents with a run line of their own (`skip`, the agents add-on's status line) are left out.
 */
export function AgentWorkingLine({
  messages,
  skip = [],
}: {
  messages: readonly MessageView[];
  skip?: readonly string[];
}) {
  // Every render reads the clock (a late-arriving old ask is judged against now); while someone
  // works, a slow tick re-renders so an ask past its window (a lost reply) retires.
  const [, setTick] = useState(0);
  const working = workingAgents(messages, new Date()).filter((a) => !skip.includes(a.id));
  const any = working.length > 0;
  useEffect(() => {
    if (!any) return;
    const t = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, [any]);
  if (!any) return null;
  const list = (names: string[]) =>
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  // DE-QUICK-ACK: "is working…" once a bridge or the runtime picked the ask up (its run is
  // checked out); "Waking …" while it still waits to be picked up.
  const busy = working.filter((a) => a.running).map((a) => a.name);
  const waking = working.filter((a) => !a.running).map((a) => a.name);
  return (
    <div
      data-slot="agent-working"
      data-state={busy.length ? "running" : "queued"}
      role="status"
      aria-live="polite"
      className="flex min-w-0 items-center gap-2 px-1 text-xs text-muted-foreground"
    >
      {/* Not a Spinner: that announces "Loading", and an agent at work is not a page load. */}
      <LoaderCircleIcon aria-hidden="true" className="size-3 shrink-0 animate-spin" />
      <span className="min-w-0 truncate">
        {busy.length ? (
          <>
            <span className="font-medium text-secondary">{list(busy)}</span>{" "}
            {busy.length === 1 ? "is" : "are"} working…
          </>
        ) : null}
        {busy.length && waking.length ? " · " : null}
        {waking.length ? (
          <>
            Waking <span className="font-medium text-secondary">{list(waking)}</span>…
          </>
        ) : null}
      </span>
    </div>
  );
}

/**
 * CHAT-DE: "Riley isn't in #brief. Add them? [Add] [Just this once] [No]" — for the author only.
 * A DE that cannot read the channel (private) gets "can't see" and no "Just this once".
 */
function NotInChannelPrompt({
  wake,
  channelName,
  busy,
  onAdd,
  onOnce,
  onNo,
  blind,
}: {
  wake: AgentWake;
  channelName: string;
  busy: boolean;
  onAdd: () => void;
  onOnce: () => void;
  onNo: () => void;
  /** A private channel: a DE outside it cannot read it (whatever the ask's own refusal). */
  blind: boolean;
}) {
  return (
    <fieldset
      data-slot="de-prompt"
      aria-label={`${wake.name} isn't in #${channelName}`}
      className="m-0 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 rounded-md border border-border-subtle bg-sunken px-2 py-1 text-xs"
    >
      <span className="text-foreground">
        <span className="font-medium">{wake.name}</span>{" "}
        {blind ? `can't see #${channelName}.` : `isn't in #${channelName}.`} Add them?
      </span>
      <Button
        size="xs"
        variant="secondary"
        disabled={busy}
        aria-label={`Add ${wake.name} to #${channelName}`}
        onClick={onAdd}
      >
        <UserPlusIcon data-icon="inline-start" aria-hidden="true" />
        Add
      </Button>
      {blind ? null : (
        <Button size="xs" variant="ghost" disabled={busy} onClick={onOnce}>
          Just this once
        </Button>
      )}
      <Button
        size="xs"
        variant="ghost"
        disabled={busy}
        aria-label={`No, don't ask about ${wake.name} here again`}
        onClick={onNo}
      >
        No
      </Button>
      <InfoTip label="About adding a digital employee">
        {blind
          ? `Added, ${wake.name} can read #${channelName} and is asked about this message again. No: it stays out and this stops asking.`
          : `Added, ${wake.name} is woken here by the channel's rule. Just this once: this message's ask stands, without it joining. No: this stops asking.`}{" "}
        Only you see this.
      </InfoTip>
    </fieldset>
  );
}
